import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Briefcase, FileText, Users, Shield, CheckCircle, Link as LinkIcon, HelpCircle } from 'lucide-react';

const EmployerInstructions = ({ isCollapsed = true }) => {
  const [expanded, setExpanded] = useState(!isCollapsed);

  return (
    <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl border border-primary/20 mb-6 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-primary/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <HelpCircle className="w-5 h-5 text-primary" />
          <span className="font-semibold text-gray-900">Welcome to Your Employer Dashboard</span>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="px-6 pb-6 space-y-6">
          <p className="text-gray-600">
            Thank you for becoming a verified employer on INeedASocialWorker.com — the trusted platform built exclusively for licensed social workers.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* What You Can Do */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                From this dashboard, you can:
              </h4>
              <ul className="space-y-2 text-sm text-gray-600 ml-6">
                <li>• Post new job openings</li>
                <li>• Edit or manage active listings</li>
                <li>• View applicant submissions</li>
                <li>• Upgrade your subscription plan</li>
                <li>• Message candidates directly</li>
              </ul>
            </div>

            {/* How to Post a Job */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-primary" />
                How to Post a Job
              </h4>
              <ul className="space-y-2 text-sm text-gray-600 ml-6">
                <li>1. Click "Post New Job"</li>
                <li>2. Enter job details (title, location, salary)</li>
                <li>3. Select license requirements (LCSW, LMSW, LBSW)</li>
                <li>4. Choose application method</li>
                <li>5. Submit for review (live within 24 hours)</li>
              </ul>
            </div>
          </div>

          {/* Application Methods */}
          <div className="bg-white rounded-lg p-4 border border-gray-100">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
              <LinkIcon className="w-4 h-4 text-secondary" />
              Choose Application Method
            </h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-600">
                  <strong>External Link:</strong> Provide your company's official application URL
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-600">
                  <strong>Universal Apply:</strong> Accept applications directly through our platform
                </span>
              </div>
            </div>
          </div>

          {/* Managing Applicants */}
          <div className="bg-white rounded-lg p-4 border border-gray-100">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-primary" />
              Managing Applicants
            </h4>
            <p className="text-sm text-gray-600 mb-2">
              If using Universal Apply, click "View Applicants" to:
            </p>
            <ul className="text-sm text-gray-600 ml-4 space-y-1">
              <li>• Download resumes</li>
              <li>• View applicant profiles & license status</li>
              <li>• Contact candidates directly</li>
              <li>• Mark candidates as Reviewed</li>
            </ul>
          </div>

          {/* Verified Badge */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4" />
              Verified Employer Badge
            </h4>
            <p className="text-sm text-green-700">
              Employers who submit official website, company email domain, and business verification receive a "Verified Employer" badge for increased visibility and trust with candidates.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerInstructions;
